import fs from "node:fs"
import path from "node:path"

export interface TemplateInfo {
  description: string
  files: string[]
  name: string
  thumbnail?: string
}

interface ItemsObject {
  [itemKey: string]: TemplateInfo
}

/**
 * Converts folder name to spaced title case
 * e.g., "AppShell" -> "App Shell", "PortalFeatureDetail" -> "Portal Feature Detail"
 */
export function folderNameToDisplayName(folderName: string): string {
  return folderName
    .replaceAll(/[-_]/g, " ") // kebab-case and snake_case → spaces
    .replaceAll(/([A-Z])/g, " $1") // PascalCase/camelCase → spaces before uppercase
    .trim()
    .replaceAll(/\s+/g, " ") // normalize multiple spaces
    .replaceAll(/\b\w/g, c => c.toUpperCase()) // capitalize each word
}

/**
 * Recursively get all files in a directory relative to that directory
 */
const filesToIgnore = ["README.md", ".DS_Store"]

function getAllFilesInDirectory(dirPath: string, relativeTo: string): string[] {
  return fs
    .readdirSync(dirPath, { recursive: true, withFileTypes: true })
    .filter(entry => entry.isFile() && !filesToIgnore.includes(entry.name))
    .map(entry => path.relative(relativeTo, path.join(entry.parentPath || dirPath, entry.name)))
    .sort()
}

/**
 * Check for thumbnail files in a directory
 */
function findThumbnail(templateDir: string): string | undefined {
  return [".png", ".svg", ".jpg", ".jpeg"]
    .map(ext => `thumbnail${ext}`)
    .find(filename => fs.existsSync(path.join(templateDir, filename)))
}

/**
 * Parse description from a README.md file in any directory
 * Takes the first sentence after the first # heading
 *
 * @param readmeFilePath - Full path to README.md file
 * @returns string - The description or empty string if not found
 */
function parseDescriptionFromReadme(readmeFilePath: string): string {
  if (!fs.existsSync(readmeFilePath)) return ""

  try {
    const content = fs.readFileSync(readmeFilePath, "utf8")

    // Find first paragraph after first # heading
    const match = content.match(/^#[^\n]*\n+([^\n#]+)/m)
    if (!match) return ""

    let description = match[1].trim()

    // Clean up the description
    description = description.replaceAll("**", "").replaceAll("*", "") // Remove markdown formatting
    description = description.charAt(0).toUpperCase() + description.slice(1) // Ensure capital letter

    // Ensure it ends with period if it doesn't already
    if (!description.endsWith(".") && !description.endsWith("!") && !description.endsWith("?")) {
      description += "."
    }

    return description
  } catch {
    return ""
  }
}

/**
 * Parse TypeScript/TSX files for import statements and extract file dependencies
 * @param filePath - Path to .ts/.tsx file
 * @param baseDir - Base directory for resolving relative imports
 * @returns string[] - Array of imported file paths (resolved to project root)
 */
function parseImportsFromTSFile(filePath: string, baseDir: string): string[] {
  if (!fs.existsSync(filePath)) return []

  try {
    const content = fs.readFileSync(filePath, "utf8")
    const dependencies: string[] = []

    // Regex to match import statements
    const importRegex = /import\s+(?:[\w*{}\s,]+\s+from\s+)?['"`]([^'"`]+)['"`]/g

    let match
    while ((match = importRegex.exec(content)) !== null) {
      const importPath = match[1]

      // Only process relative imports (starting with ./ or ../)
      if (importPath.startsWith("./") || importPath.startsWith("../")) {
        try {
          // Resolve relative to the file's directory
          const fileDir = path.dirname(filePath)
          const resolvedPath = path.resolve(fileDir, importPath)

          // Try common extensions if no extension provided,
          // and also handle .js → .ts/.tsx remapping (bundlers resolve .js imports to .ts files)
          const withoutJs = resolvedPath.endsWith(".js") ? resolvedPath.slice(0, -3) : null
          const possiblePaths = [
            resolvedPath,
            resolvedPath + ".tsx",
            resolvedPath + ".ts",
            resolvedPath + ".json",
            resolvedPath + ".js",
            ...(withoutJs ? [withoutJs + ".ts", withoutJs + ".tsx", withoutJs] : []),
          ]

          const actualPath = possiblePaths.find(p => fs.existsSync(p))

          if (actualPath) {
            // Convert to path relative to container root
            const containerRoot = path.resolve(baseDir, "../../..")
            const relativePath = path.relative(containerRoot, actualPath).replaceAll("\\", "/")

            // Only include files within the supernova directory
            if (relativePath.startsWith("supernova/")) {
              dependencies.push(relativePath)
            }
          }
        } catch {
          // Skip invalid import paths
        }
      }
    }

    return dependencies
  } catch {
    return []
  }
}

/**
 * Parse all TypeScript/TSX files in a directory for import dependencies
 * @param itemDir - Directory containing the template/pattern
 * @returns string[] - Array of all imported file paths from all TS/TSX files
 */
function parseImportsFromDirectory(itemDir: string): string[] {
  const dependencies = new Set<string>()

  try {
    const entries = fs.readdirSync(itemDir, { recursive: true, withFileTypes: true })

    for (const entry of entries) {
      if (entry.isFile()) {
        const ext = path.extname(entry.name).toLowerCase()
        if (ext === ".ts" || ext === ".tsx") {
          const fullPath = path.join(entry.parentPath || itemDir, entry.name)
          const fileDeps = parseImportsFromTSFile(fullPath, itemDir)
          for (const dep of fileDeps) dependencies.add(dep)
        }
      }
    }
  } catch {
    // Skip directories that can't be read
  }

  return [...dependencies].sort()
}

/**
 * Parse main directory README.md for table-based descriptions
 * Extracts description from markdown table like:
 * | Pattern | Description |
 * | PromptEditor | Tiptap rich text editor |
 */
function parseMainReadmeTable(mainReadmePath: string, itemKey: string): string | null {
  if (!fs.existsSync(mainReadmePath)) return null

  try {
    const content = fs.readFileSync(mainReadmePath, "utf8")

    // Find table that contains Pattern or Template header
    const tableMatch = content.match(/(\|\s*(?:Pattern|Template)\s*\|[^\n]*)\n\|[-\s|]*\n((?:\|[^\n]*\n)*)/i)
    if (!tableMatch) return null

    // Parse header to get column indices
    const headerCells = tableMatch[1]
      .split("|")
      .map(cell => cell.trim())
      .filter(Boolean)
    const nameIndex = headerCells.findIndex(cell => /^(Pattern|Template)$/i.test(cell))
    const descIndex = headerCells.findIndex(cell => /^Description$/i.test(cell))

    if (nameIndex === -1 || descIndex === -1) return null

    // Parse table rows
    const rows = tableMatch[2].split("\n").filter(row => row.trim())

    for (const row of rows) {
      const cells = row
        .split("|")
        .map(cell => cell.trim())
        .filter(Boolean)

      if (cells.length > Math.max(nameIndex, descIndex)) {
        const name = cells[nameIndex]?.replace(/\[([^\]]+)\].*/, "$1") // Remove markdown link syntax
        const description = cells[descIndex]

        if (name === itemKey) {
          return description.endsWith(".") ? description : description + "."
        }
      }
    }
  } catch {
    // If error reading file, return null
  }

  return null
}

/**
 * Extract description from README.md file
 * Priority: 1) Individual README, 2) Main directory table, 3) Fallback
 */
function extractDescriptionFromReadme(
  itemDir: string,
  fallbackName: string,
  itemKey: string,
  subdirectory: "templates" | "patterns",
): string {
  // Try individual README first (highest priority)
  const itemReadmePath = path.join(itemDir, "README.md")
  const individualDescription = parseDescriptionFromReadme(itemReadmePath)
  if (individualDescription) {
    return individualDescription
  }

  // Try main directory table second
  const containerRoot = path.resolve(itemDir, "../../..")
  const mainReadmePath = path.join(containerRoot, "supernova", subdirectory, "README.md")
  const tableDescription = parseMainReadmeTable(mainReadmePath, itemKey)
  if (tableDescription) {
    return tableDescription
  }

  // Fall back to default
  return `Use this to prototype similar to ${fallbackName}`
}

/**
 * Read package.json and return parsed content
 */
function readPackageJson(basePath: string): any {
  const packageJsonPath = path.join(basePath, "package.json")

  if (!fs.existsSync(packageJsonPath)) {
    throw new Error(`package.json not found at ${packageJsonPath}`)
  }

  const content = fs.readFileSync(packageJsonPath, "utf8")
  return JSON.parse(content)
}

/**
 * Write package.json with proper formatting
 */
function writePackageJson(basePath: string, packageData: any): void {
  const packageJsonPath = path.join(basePath, "package.json")
  const content = JSON.stringify(packageData, null, 2) + "\n"
  fs.writeFileSync(packageJsonPath, content, "utf8")
}

/**
 * Discovers items in a supernova subdirectory and returns metadata object
 *
 * @param basePath - Base directory path (defaults to current working directory)
 * @param subdirectory - Subdirectory name ('templates' or 'patterns')
 * @returns ItemsObject - Object with item metadata
 */
function discoverItems(basePath: string, subdirectory: "templates" | "patterns"): ItemsObject {
  const resolvedBasePath = basePath || process.cwd()
  const itemsDir = path.join(resolvedBasePath, "supernova", subdirectory)

  // Return empty if directory doesn't exist — not all projects have both templates and patterns
  if (!fs.existsSync(itemsDir)) {
    return {}
  }

  const items: ItemsObject = {}

  // Read all entries in directory
  const entries = fs.readdirSync(itemsDir, { withFileTypes: true })

  for (const entry of entries) {
    // Skip non-directories and README files
    if (!entry.isDirectory() || entry.name === "README.md") {
      continue
    }

    const itemKey = entry.name
    const itemDir = path.join(itemsDir, itemKey)

    // Generate display name
    const displayName = folderNameToDisplayName(itemKey)

    // Extract description from README.md
    const description = extractDescriptionFromReadme(itemDir, displayName, itemKey, subdirectory)

    // Find thumbnail
    const thumbnail = findThumbnail(itemDir)

    // Get all files in item directory with full paths
    const files = getAllFilesInDirectory(itemDir, itemDir)
    const containerRoot = path.resolve(itemDir, "../../..") // This goes from supernova/templates/X to container root
    const itemRelativePath = path.relative(containerRoot, itemDir).replaceAll("\\", "/")
    const fullPathFiles = files.map(file => `${itemRelativePath}/${file}`)

    // Parse dependencies from TypeScript/TSX import statements
    const importDependencies = parseImportsFromDirectory(itemDir)

    // Combine both sources of dependencies
    const allDependencies = [...new Set(importDependencies)]

    // Filter out dependencies that are within the same item directory
    const itemBasePath = itemRelativePath
    const externalDependencies = allDependencies.filter(dep => !dep.startsWith(itemBasePath + "/"))

    // Validate that external dependencies exist in the filesystem
    const validExternalDependencies = externalDependencies.filter(dep => {
      const fullPath = path.join(containerRoot, dep)
      const exists = fs.existsSync(fullPath)
      if (!exists) {
        console.warn(`Warning: Dependency not found: ${dep} (expected at ${fullPath})`)
      }

      return exists
    })

    // Combine files and valid dependencies, remove duplicates
    const allFiles = new Set([...fullPathFiles, ...validExternalDependencies])
    // Remove README.md files and thumbnail files from the combined list
    const filteredFiles = [...allFiles].filter(
      file =>
        !file.endsWith("README.md") &&
        !["/thumbnail.png", "/thumbnail.svg", "/thumbnail.jpg", "/thumbnail.jpeg"].some(ext => file.endsWith(ext)),
    )

    // Validate that all files exist in the filesystem
    const existingFiles = filteredFiles
      .filter(file => {
        const fullPath = path.join(containerRoot, file)
        const exists = fs.existsSync(fullPath)
        if (!exists) {
          console.warn(`Warning: File not found: ${file} (expected at ${fullPath})`)
        }

        return exists
      })
      .sort()

    // Build item info object
    const itemInfo: TemplateInfo = {
      name: displayName,
      description,
      files: existingFiles,
    }

    // Only add thumbnail property if it exists (with full path)
    if (thumbnail) {
      itemInfo.thumbnail = `${itemRelativePath}/${thumbnail}`
    }

    items[itemKey] = itemInfo
  }

  return items
}

/**
 * Discovers templates in supernova/templates directory and returns metadata object
 *
 * @param basePath - Base directory path (defaults to current working directory)
 * @returns ItemsObject - Object with template metadata
 */
function discoverTemplates(basePath?: string): ItemsObject {
  const resolvedBasePath = basePath || process.cwd()
  return discoverItems(resolvedBasePath, "templates")
}

/**
 * Discovers patterns in supernova/patterns directory and returns metadata object
 *
 * @param basePath - Base directory path (defaults to current working directory)
 * @returns ItemsObject - Object with pattern metadata
 */
function discoverPatterns(basePath?: string): ItemsObject {
  const resolvedBasePath = basePath || process.cwd()
  return discoverItems(resolvedBasePath, "patterns")
}

/**
 * Resolve transitive dependencies for templates/patterns
 * If Template A imports Pattern B, and Pattern B imports Pattern C,
 * then Template A should include files from both Pattern B and Pattern C
 */
function resolveTransitiveDependencies(items: ItemsObject, patterns: ItemsObject, basePath: string): ItemsObject {
  const resolvedItems: ItemsObject = {}

  for (const [itemKey, itemInfo] of Object.entries(items)) {
    const resolvedFiles = new Set(itemInfo.files)

    // Find which patterns this item depends on
    const dependentPatterns = new Set<string>()

    // Check direct dependencies by looking at file paths
    for (const file of itemInfo.files) {
      const pathMatch = file.match(/^supernova\/patterns\/([^\/]+)\//)
      if (pathMatch) {
        dependentPatterns.add(pathMatch[1])
      }
    }

    // Recursively resolve transitive dependencies
    function addTransitiveDependencies(patternKey: string, visited = new Set<string>()) {
      if (visited.has(patternKey)) {
        return // Avoid circular dependencies
      }

      visited.add(patternKey)

      const pattern = patterns[patternKey]
      if (!pattern) {
        return
      }

      // Add all files from this pattern
      for (const file of pattern.files) resolvedFiles.add(file)

      // Find what patterns this pattern depends on
      for (const file of pattern.files) {
        const pathMatch = file.match(/^supernova\/patterns\/([^\/]+)\//)
        if (pathMatch && pathMatch[1] !== patternKey) {
          // Recursively add transitive dependencies
          addTransitiveDependencies(pathMatch[1], new Set(visited))
        }
      }
    }

    // Resolve transitive dependencies for each direct dependency
    for (const patternKey of dependentPatterns) {
      addTransitiveDependencies(patternKey)
    }

    // Validate all resolved files exist
    const containerRoot = path.resolve(basePath)
    const validFiles = [...resolvedFiles]
      .filter(file => {
        const fullPath = path.join(containerRoot, file)
        const exists = fs.existsSync(fullPath)
        if (!exists) {
          console.warn(`Warning: Transitive dependency not found: ${file} (expected at ${fullPath})`)
        }

        return exists
      })
      .sort()

    resolvedItems[itemKey] = {
      ...itemInfo,
      files: validFiles,
    }
  }

  return resolvedItems
}

/**
 * Discovers templates and patterns, then updates package.json with the results
 *
 * @param basePath - Base directory path (defaults to current working directory)
 * @returns Promise<{templates: ItemsObject, patterns: ItemsObject}> - Object with template and pattern metadata
 */

export async function discoverTemplatesAndPatterns(
  basePath?: string,
): Promise<{ templates: ItemsObject; patterns: ItemsObject }> {
  const resolvedBasePath = basePath || process.cwd()

  // First discover templates and patterns independently
  const rawTemplates = discoverTemplates(resolvedBasePath)
  const rawPatterns = discoverPatterns(resolvedBasePath)

  // Resolve transitive dependencies for templates
  const templates = resolveTransitiveDependencies(rawTemplates, rawPatterns, resolvedBasePath)

  // Resolve transitive dependencies for patterns (in case patterns depend on other patterns)
  const patterns = resolveTransitiveDependencies(rawPatterns, rawPatterns, resolvedBasePath)

  return { templates, patterns }
}

export async function discoverFilesForTemplates(
  pkgTemplates: Record<string, Pick<TemplateInfo, "name" | "description">>,
  basePath?: string,
): Promise<ItemsObject> {
  const { templates } = await discoverTemplatesAndPatterns(basePath)
  return Object.fromEntries(
    Object.entries(pkgTemplates).map(([key, pkgTemplate]) => [key, { ...templates[key], ...pkgTemplate }]),
  )
}

export async function discoverAndUpdatePackageJson(
  basePath?: string,
): Promise<{ templates: ItemsObject; patterns: ItemsObject }> {
  const result = await discoverTemplatesAndPatterns(basePath)
  const resolvedBasePath = basePath || process.cwd()

  // Read current package.json
  const packageData = readPackageJson(resolvedBasePath)

  // Ensure supernova object exists
  if (!packageData.supernova) {
    packageData.supernova = {}
  }

  // Only persist name and description — files and thumbnail are always re-discovered at upload time
  packageData.supernova.templates = Object.fromEntries(
    Object.entries(result.templates).map(([key, { name, description }]) => [key, { name, description }]),
  )

  // Write back to package.json
  writePackageJson(resolvedBasePath, packageData)

  return result
}
